import React from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';

const DataApp = () => {
  const navigate = useNavigate();

  const dataCategories = [
    {
      id: 'song-releases',
      title: 'Song Releases (Master Side)',
      description: 'Import past and upcoming song releases from your Artist project to fill out the master side of your catalog.',
      icon: (
        <svg className="w-6 h-6 text-[#1DB954]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      ),
      color: '#1DB954',
      route: '/data/song-releases'
    },
    {
      id: 'writer-credits',
      title: 'Writer Credits (Publishing Side)',
      description: 'Import songwriting credits and splits to fill out the publishing side of your catalog.',
      icon: (
        <svg className="w-6 h-6 text-[#FF6B35]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      ),
      color: '#FF6B35',
      route: '/data/writer-credits'
    },
    {
      id: 'live-shows',
      title: 'Live Shows',
      description: 'Import past and upcoming live shows to fill out the bookings.',
      icon: (
        <svg className="w-6 h-6 text-[#00CED1]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
        </svg>
      ),
      color: '#00CED1',
      route: '/data/live-shows'
    },
    {
      id: 'ticket-counts',
      title: 'Ticket Counts',
      description: 'Add the latest ticket counts for upcoming shows.',
      icon: (
        <svg className="w-6 h-6 text-[#E040FB]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
        </svg>
      ),
      color: '#E040FB',
      route: '/data/ticket-tracking'
    }
  ];

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Data</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => {
                const canGoBack = window.history.length > 1;
                if (canGoBack) {
                  navigate(-1);
                } else {
                  navigate('/');
                }
              }}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {/* Description */}
            <div className="mb-8">
              <p className="text-[rgba(255,255,255,0.7)] text-[12px] leading-[1.6]">
                Import your music and touring data and set up an automatic flow of information from the tools you already use the most directly into Dizee.
              </p>
            </div>

            {/* Data Categories Grid */}
            <div className="space-y-4">
              {dataCategories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => navigate(category.route)}
                  className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-xl p-4 hover:border-[rgba(255,255,255,0.25)] hover:bg-[rgba(255,255,255,0.05)] cursor-pointer transition-all duration-200"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border border-[rgba(255,255,255,0.1)]"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      {category.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white text-[14px] font-medium mb-1.5">
                        {category.title}
                      </h3>
                      <p className="text-[rgba(255,255,255,0.55)] text-[12px] leading-[1.5]">
                        {category.description}
                      </p>
                    </div>

                    {/* Arrow */}
                    <div className="flex-shrink-0 mt-3">
                      <svg className="w-4 h-4 text-[rgba(255,255,255,0.3)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default DataApp;

